import {
  IconAngle,
  IconBuildingStore,
  IconCertificate,
  IconChisel,
  IconWeight,
  IconPrinter,
  IconCut,
  IconDropletMinus,
  IconFence,
  IconLayersIntersect2,
  IconPlant2,
  IconReorder,
  IconWood,
  IconWash,
  IconFlask,
} from "@tabler/icons-react"
import { IconProps } from "@tfg/ui"
import React from "react"
import { useTranslation } from "react-i18next"

import { IconAppret } from "~/components/icons/activities/IconAppret"
import { IconComponentProvider } from "~/components/icons/activities/IconComponentProvider"
import { IconDyeing } from "~/components/icons/activities/IconDyeing"
import { IconFootwearAssembling } from "~/components/icons/activities/IconFootwearAssembling"
import { IconFootwearFinishing } from "~/components/icons/activities/IconFootwearFinishing"
import { IconLamination } from "~/components/icons/activities/IconLamination"
import { IconManufacturing } from "~/components/icons/activities/IconManufacturing"
import { IconRawMaterial } from "~/components/icons/activities/IconRawMaterial"
import { IconSpinning } from "~/components/icons/activities/IconSpinning"
import { IconStitching } from "~/components/icons/activities/IconStitching"
import { IconWeaving } from "~/components/icons/activities/IconWeaving"
import { i18n } from "~/i18n"

export const useGetOperations = (): Record<
  string,
  {
    name: string
    icon: React.FC<IconProps> | null
  }
> => {
  const { t } = useTranslation(["traceability"])
  return {
    BRAND: {
      name: t("traceability:activities.brand"),
      icon: IconBuildingStore,
    },
    CERTIFIED_ENTITY: {
      name: i18n.t("traceability:activities.certifiedEntity"),
      icon: IconCertificate,
    },
    APPAREL_MANUFACTURING: {
      name: i18n.t("traceability:activities.apparel.manufacturing"),
      icon: IconManufacturing,
    },
    FABRIC_WEAVING_OR_KNITTING: {
      name: i18n.t("traceability:activities.apparel.fabric.weavingKnitting"),
      icon: IconWeaving,
    },
    FABRIC_DYEING_PRINTING: {
      name: i18n.t("traceability:activities.apparel.fabric.dyeing"),
      icon: IconDyeing,
    },
    FABRIC_SPINNING: {
      name: i18n.t("traceability:activities.apparel.fabric.spinning"),
      icon: IconSpinning,
    },
    FABRIC_APPRET: {
      name: i18n.t("traceability:activities.apparel.fabric.appret"),
      icon: IconAppret,
    },
    FABRIC_LAMINATION: {
      name: i18n.t("traceability:activities.apparel.fabric.lamination"),
      icon: IconLamination,
    },
    FABRIC_MATERIAL_PRODUCTION: {
      name: i18n.t("traceability:activities.apparel.fabric.materialProduction"),
      icon: IconRawMaterial,
    },
    YARN_PROVIDER: {
      name: i18n.t("traceability:activities.apparel.yarn.spinning"),
      icon: IconSpinning,
    },
    YARN_DYEING_PRINTING: {
      name: i18n.t("traceability:activities.apparel.yarn.dyeing"),
      icon: IconDyeing,
    },
    LEATHER_TANNING: {
      name: i18n.t("traceability:activities.apparel.leather.tanning"),
      icon: IconAppret,
    },
    YARN_MATERIAL_PRODUCTION: {
      name: i18n.t("traceability:activities.apparel.yarn.materialProduction"),
      icon: IconRawMaterial,
    },
    LEATHER_DYEING_PRINTING: {
      name: i18n.t("traceability:activities.apparel.leather.dyeing"),
      icon: IconDyeing,
    },
    LEATHER_MATERIAL_PRODUCTION: {
      name: i18n.t(
        "traceability:activities.apparel.leather.materialProduction"
      ),
      icon: IconFence,
    },
    LEATHER_APPRET: {
      name: i18n.t("traceability:activities.apparel.leather.appret"),
      icon: IconAppret,
    },
    LEATHER_FARM: {
      name: i18n.t("traceability:activities.apparel.leather.farm"),
      icon: null,
    },
    FOOTWEAR_FINISHING: {
      name: i18n.t("traceability:activities.footwear.finishing"),
      icon: IconFootwearFinishing,
    },
    FOOTWEAR_STITCHING: {
      name: i18n.t("traceability:activities.footwear.stitching"),
      icon: IconStitching,
    },
    FOOTWEAR_ASSEMBLING: {
      name: i18n.t("traceability:activities.footwear.assembling"),
      icon: IconFootwearAssembling,
    },
    ACCESSORY_PROVIDER: {
      name: i18n.t("traceability:activities.accessoryProvider"),
      icon: IconComponentProvider,
    },
    ACCESSORY_RAW_MATERIAL_PROVIDER: {
      name: i18n.t("traceability:activities.accessoryRawMaterialProvider"),
      icon: IconRawMaterial,
    },
    DUVET_BAFFLE: {
      name: i18n.t("traceability:activities.apparel.duvet.baffle"),
      icon: null,
    },
    DUVET_PRE_PROCESSOR: {
      name: i18n.t("traceability:activities.apparel.duvet.preProcessor"),
      icon: null,
    },
    DUVET_FARM: {
      name: i18n.t("traceability:activities.apparel.duvet.farm"),
      icon: null,
    },
    DUVET_MATERIAL_PRODUCTION: {
      name: i18n.t("traceability:activities.apparel.duvet.materialProduction"),
      icon: IconRawMaterial,
    },
    WOOD_HARVESTING: {
      name: i18n.t("traceability:activities.wood.harvesting"),
      icon: IconPlant2,
    },
    WOOD_ASSEMBLING: {
      name: i18n.t("traceability:activities.wood.assembling"),
      icon: IconLayersIntersect2,
    },
    WOOD_MANUFACTURING: {
      name: i18n.t("traceability:activities.wood.manufacturing"),
      icon: IconManufacturing,
    },
    WOOD_BRUSHING: {
      name: i18n.t("traceability:activities.wood.brushing"),
      icon: IconPlant2,
    },
    WOOD_SANDING: {
      name: i18n.t("traceability:activities.wood.sanding"),
      icon: IconChisel,
    },
    WOOD_CALIBRATION_AND_COLLAGE: {
      name: i18n.t("traceability:activities.wood.calibrationAndCollage"),
      icon: IconAngle,
    },
    WOOD_MATERIAL_PEEKING: {
      name: i18n.t("traceability:activities.wood.materialPeeking"),
      icon: IconReorder,
    },
    WOOD_MATERIAL_PEELING: {
      name: i18n.t("traceability:activities.wood.materialPeeling"),
      icon: IconWood,
    },
    WOOD_MATERIAL_SAWING: {
      name: i18n.t("traceability:activities.wood.materialSawing"),
      icon: IconCut,
    },
    WOOD_MATERIAL_DRYING: {
      name: i18n.t("traceability:activities.wood.materialDrying"),
      icon: IconDropletMinus,
    },
    PAPER_ASSEMBLING: {
      name: i18n.t("traceability:activities.paper.assembling"),
      icon: IconLayersIntersect2,
    },
    PAPER_MANUFACTURING: {
      name: i18n.t("traceability:activities.paper.manufacturing"),
      icon: IconManufacturing,
    },
    PAPER_PRINTING: {
      name: i18n.t("traceability:activities.paper.printing"),
      icon: IconPrinter,
    },
    PAPER_PRESSING: {
      name: i18n.t("traceability:activities.paper.pressing"),
      icon: IconWeight,
    },
    PAPER_LAMINATING: {
      name: i18n.t("traceability:activities.paper.laminating"),
      icon: IconLamination,
    },
    PAPER_BLEACHING: {
      name: i18n.t("traceability:activities.paper.bleaching"),
      icon: IconPlant2,
    },
    PAPER_RESINE_REMOVING: {
      name: i18n.t("traceability:activities.paper.resineRemoving"),
      icon: IconPlant2,
    },
    PAPER_CHEMICAL_TREATMENT: {
      name: i18n.t("traceability:activities.paper.chemicalTreatment"),
      icon: IconFlask,
    },
    PAPER_WHITE_WASHING: {
      name: i18n.t("traceability:activities.paper.whiteWashing"),
      icon: IconWash,
    },
  }
}
